import { useEffect, useState } from 'react';
import { Archive, ArchiveRestore, ArrowLeft, MessagesSquare, Trash2 } from 'lucide-react';
import { Permission, type Channel } from '@spawnwire/shared';
import { EmptyState, IconButton, Spinner } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { attempt } from '../../lib/data';
import { can, useChannelPermissions } from '../../lib/perms';
import { actions, useStore } from '../../store/store';
import { ui, useUi } from '../../store/ui';
import { Composer } from './Composer';
import { MessageList } from './MessageList';
import { PanelHeader } from './PanelHeader';
import { TypingIndicator } from './TypingIndicator';

function ThreadList({ parentId }: { parentId: string }) {
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const channels = useStore((s) => s.channels);
  const threads = Object.values(channels)
    .filter((c) => c.type === 'thread' && c.parentId === parentId)
    .sort((a, b) => (b.lastMessageId ?? b.createdAt).localeCompare(a.lastMessageId ?? a.createdAt));

  useEffect(() => {
    http
      .get<Channel[]>(`/channels/${parentId}/threads`)
      .then((list) => {
        list.forEach((c) => actions.upsertChannel(c));
        setLoaded(true);
      })
      .catch((err) => setError(errorMessage(err)));
  }, [parentId]);

  return (
    <>
      <PanelHeader title="Threads" icon={<MessagesSquare className="size-5 text-faint" aria-hidden />} />
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto p-2">
        {error && <p className="p-3 text-sm text-danger">{error}</p>}
        {!loaded && !error && (
          <div className="flex justify-center p-6">
            <Spinner />
          </div>
        )}
        {loaded && threads.length === 0 && <EmptyState icon={MessagesSquare} title="No threads yet" body="Start a thread from any message to keep side conversations tidy." />}
        <ul className="space-y-1">
          {threads.map((t) => (
            <li key={t.id}>
              <button type="button" onClick={() => ui.openThread(t.id)} className="w-full rounded-md px-3 py-2 text-left hover:bg-hover">
                <div className="truncate font-medium text-fg">{t.name}</div>
                <div className="text-xs text-muted">
                  {t.messageCount ?? 0} message{t.messageCount === 1 ? '' : 's'}
                  {t.archived ? ' · archived' : ''}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

export function ThreadPanel({ parentId }: { parentId: string }) {
  const threadId = useUi((s) => s.threadId);
  const thread = useStore((s) => (threadId ? s.channels[threadId] : undefined));
  const meId = useStore((s) => s.me?.id);
  const perms = useChannelPermissions(parentId);
  const [loading, setLoading] = useState(false);

  // A thread opened from a message summary may not be in the store yet.
  useEffect(() => {
    if (!threadId || thread) return;
    setLoading(true);
    http
      .get<Channel>(`/channels/${threadId}`)
      .then((c) => actions.upsertChannel(c))
      .catch(() => useUi.setState({ threadId: null }))
      .finally(() => setLoading(false));
  }, [threadId, thread]);

  if (!threadId || (thread && thread.parentId !== parentId)) return <ThreadList parentId={parentId} />;
  if (!thread) {
    return (
      <div className="flex flex-1 items-center justify-center">{loading ? <Spinner /> : null}</div>
    );
  }
  const canManage = thread.ownerId === meId || can(perms, Permission.MANAGE_MESSAGES);

  return (
    <>
      <PanelHeader
        title={thread.name}
        icon={<IconButton icon={ArrowLeft} label="All threads" onClick={() => useUi.setState({ threadId: null })} />}
        actions={
          canManage && (
            <>
              <IconButton
                icon={thread.archived ? ArchiveRestore : Archive}
                label={thread.archived ? 'Unarchive thread' : 'Archive thread'}
                onClick={() => void attempt(() => http.patch(`/channels/${thread.id}`, { archived: !thread.archived }))}
              />
              <IconButton
                icon={Trash2}
                label="Delete thread"
                danger
                onClick={() =>
                  ui.confirm({
                    title: `Delete thread "${thread.name}"?`,
                    body: 'Every message in the thread will be deleted.',
                    confirmLabel: 'Delete thread',
                    danger: true,
                    onConfirm: async () => {
                      await http.del(`/channels/${thread.id}`);
                      useUi.setState({ threadId: null });
                    },
                  })
                }
              />
            </>
          )
        }
      />
      <MessageList channelId={thread.id} />
      <div className="relative">
        <Composer channelId={thread.id} placeholder={`Reply in ${thread.name}`} />
        <div className="absolute inset-x-0 bottom-0">
          <TypingIndicator channelId={thread.id} />
        </div>
      </div>
    </>
  );
}
